import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';

import { Processes as ProcessesType, Query, StepName } from '../../design-system/types';
import { Processes } from '../../domain';
import { useTrackEventAmplitude } from '../../shared/analytics';
import { FiltersButton } from '../Filters/components/MobileFiltersButton';
import { DraggedItem } from '../../resources/pipeline/types';
import { DossierId } from '../../resources/dossier/types';
import { parseQueryString } from '../../shared/utilities/queryString';
import {
    getArchivedDossierOverviewsQueryKey,
    getDossierOverviewsQueryKey,
} from '../../resources/dossier-overview/hooks';
import {
    useDossierOverviewsFromUrlProcess,
    useFilteredDossierOverviewsByProcess,
} from '../../resources/pipeline/hooks';

import { getAgentsQueryKey, useGetAgents } from '../../resources/agent/hooks';

import { useChangeStepMutation } from '../../resources/dossier/mutations';

import { DroppableStep } from './components/step/DroppableStep';
import { ErrorOverlay } from './components/Error';
import { DndOverlayDossierOverview } from './components/DndOverlayDossierOverview';
import { computeBudgetSummary } from './utils/pipeline.utils';

import s from './Process.css';

export const Process = React.memo((): JSX.Element => {
    const location = useLocation();
    const queryClient = useQueryClient();
    const { processName } = useParams<{ processName: ProcessesType }>();
    const { trackEvent, isAmplitudeLoaded } = useTrackEventAmplitude();

    const [isMobile, setMobile] = useState(false);
    const [draggedDossierId, setDraggedDossierId] = useState<DossierId | null>(null);
    const [latestUpdated, setLatestUpdated] = useState<DossierId | null>(null);

    const agentsQuery = useGetAgents();
    const dossiersQuery = useDossierOverviewsFromUrlProcess();

    const changeStepMutation = useChangeStepMutation();

    const isError = agentsQuery.isError || dossiersQuery.isError;

    // constraint to enable clicking on the card as a link, and when moving the mouse enable the drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    );

    useEffect(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.viewedPagePipeline({
                ...defaultProperties,
                pipeline_tab: processName,
            });
        });
    }, [processName, isAmplitudeLoaded, trackEvent]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setMobile(window.innerWidth < 750);
        }
    }, [setMobile]);

    const filteredProcessDossiers = useFilteredDossierOverviewsByProcess(
        processName,
        parseQueryString(location.search) as Query,
    );

    const handleRefresh = () => {
        void queryClient.invalidateQueries({
            queryKey: getAgentsQueryKey(),
        });
        void queryClient.invalidateQueries({
            queryKey:
                processName === 'archive'
                    ? getArchivedDossierOverviewsQueryKey()
                    : getDossierOverviewsQueryKey(),
        });
    };

    const handleChangeStep = (dossierId: DossierId, nextStep: StepName) => {
        changeStepMutation.mutate({ dossierId, nextStep });
    };

    // We only want to display budget labels if there is at least one column/step with a total budget to display
    const hasDossiersWithBudgetToDisplay = useMemo(() => {
        return Processes[processName].some((step) => {
            const dossierOverviewsInStep = filteredProcessDossiers.filter(
                (dossierOverview) => dossierOverview.step === step,
            );
            const { budgetDisplay } = computeBudgetSummary(dossierOverviewsInStep);
            return budgetDisplay === 'partial' || budgetDisplay === 'all';
        });
    }, [filteredProcessDossiers, processName]);

    const handleDragStart = (event: DragStartEvent) => {
        setDraggedDossierId(String(event.active.id));
    };
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const draggedItem = event.active.data.current as DraggedItem;
        if (!over || !draggedItem.dossierCanChangeStepTo.includes(over.id as StepName)) {
            return;
        }

        const { dossierId, currentStep } = active.data.current as DraggedItem;
        const nextStep = over.id as StepName;

        if (currentStep !== nextStep) {
            handleChangeStep(dossierId, nextStep);
            setLatestUpdated(dossierId);

            trackEvent((ampli, defaultProperties) => {
                ampli.cardDraggedOrDropped({
                    ...defaultProperties,
                    request_id: parseInt(dossierId),
                    drag_column: currentStep,
                    drop_column: nextStep,
                });
            });
        }
        setDraggedDossierId(null);
    };

    return (
        <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} sensors={sensors}>
            <div className={s.pageContainer}>
                {isError ? (
                    <ErrorOverlay onRefresh={handleRefresh} />
                ) : (
                    <>
                        {Processes[processName].map((step) => (
                            <DroppableStep
                                key={step}
                                isMobile={isMobile}
                                name={step}
                                process={processName}
                                draggedDossierId={draggedDossierId}
                                latestUpdated={latestUpdated}
                                setLatestUpdated={setLatestUpdated}
                                showTotalBudgetLabel={hasDossiersWithBudgetToDisplay}
                            />
                        ))}
                        <DragOverlay>
                            {draggedDossierId ? (
                                <DndOverlayDossierOverview id={draggedDossierId} />
                            ) : null}
                        </DragOverlay>
                    </>
                )}
                <FiltersButton />
            </div>
        </DndContext>
    );
});
Process.displayName = 'Process';
